import React from 'react'
import {ColorModeContext, useColorMode} from '@chakra-ui/react'
import {Box, Button, Flex} from '../../../../utils/chakraMonomorphic'

import copy from './copy.json'

// Remembered per browser. Storage can be missing or blocked, so every read and
// write is guarded and the page falls back to light.
export const FACE_OFF_THEME_KEY = 'faceOff.theme'

const readDark = (): boolean => {
  try {
    return window.localStorage.getItem(FACE_OFF_THEME_KEY) === 'dark'
  } catch {
    return false
  }
}

const writeDark = (dark: boolean): void => {
  try {
    window.localStorage.setItem(FACE_OFF_THEME_KEY, dark ? 'dark' : 'light')
  } catch {
    // Not remembered; the toggle still works for this visit.
  }
}

// The Face Off screens read their colors from these variables, so the full
// screen review and the enlarged photo, which sit inside this scope, follow
// the toggle too. Dark values are the local Face Off app's.
const LIGHT = {
  '--fo-bg': '#ffffff',
  '--fo-text': '#1a202c',
  '--fo-muted': '#4a5568',
  '--fo-panel': '#edf2f7',
  '--fo-hover': '#f7fafc',
  '--fo-selected': '#ebf8ff',
  '--fo-accent': '#4299e1',
  '--fo-link': '#2b6cb0',
  '--fo-red': '#c53030',
  '--fo-green': '#2f855a',
  '--fo-amber': '#c05621',
  '--fo-red-line': '#fc8181',
  '--fo-amber-line': '#fbd38d',
}

const DARK = {
  '--fo-bg': '#0d0f13',
  '--fo-text': '#e6e9ef',
  '--fo-muted': '#939cad',
  '--fo-panel': '#23272f',
  '--fo-hover': '#1b1e24',
  '--fo-selected': '#1d2a3a',
  '--fo-accent': '#6aa8ff',
  '--fo-link': '#8ab4ff',
  '--fo-red': '#ff6b70',
  '--fo-green': '#3ddc84',
  '--fo-amber': '#f0b429',
  '--fo-red-line': '#e5484d',
  '--fo-amber-line': '#e0a316',
}

/**
 * The Face Off screens with their own light or dark theme. Only this subtree
 * changes; the rest of Admin keeps its theme.
 */
export const FaceOffTheme: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [dark, setDark] = React.useState(readDark)
  const outer = useColorMode()
  // Chakra's own components (buttons, tables, inputs) read this mode. The same
  // provider wraps both themes, so a toggle keeps the screen's state.
  const mode = React.useMemo(
    () => ({...outer, colorMode: dark ? 'dark' : 'light'} as const),
    [outer, dark],
  )
  const toggle = (): void => {
    setDark((d) => {
      writeDark(!d)
      return !d
    })
  }

  const body = (
    <Box
      sx={dark ? DARK : LIGHT}
      bg='var(--fo-bg)'
      color='var(--fo-text)'
      borderRadius='md'
      p={dark ? 3 : 0}
      data-theme={dark ? 'dark' : 'light'}
      data-testid='face-off-theme'>
      <Flex justifyContent='flex-end' mb={2}>
        <Button
          size='sm'
          variant='outline'
          onClick={toggle}
          aria-pressed={dark}
          data-testid='face-off-theme-toggle'>
          {dark ? copy.theme.light : copy.theme.dark}
        </Button>
      </Flex>
      {children}
    </Box>
  )
  return (
    <ColorModeContext.Provider value={mode}>{body}</ColorModeContext.Provider>
  )
}
